import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import InputBase from "@material-ui/core/InputBase";
import IconButton from "@material-ui/core/IconButton";
import Fab from "@material-ui/core/Fab";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Popover from "@material-ui/core/Popover";
import Switch from "@material-ui/core/Switch";
import Checkbox from "@material-ui/core/Checkbox";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import SearchIcon from "@material-ui/icons/Search";
import FilterListIcon from "@material-ui/icons/FilterList";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import AddIcon from "@material-ui/icons/Add";
import CheckIcon from "@material-ui/icons/Check";

import useTicketsManagerTabs from "../TicketsManagerTabs/useTicketsManagerTabs";
import { getAccessSnapshot } from "../../context/AccessControl/accessSnapshot";
import NewTicketModal from "../NewTicketModal";
import TicketsListCustom from "../TicketsListCustom";
import SearchResultsPanel from "../SearchResultsPanel";
import CloseAllTicketsModal from "../CloseAllTicketsModal";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => {
  const dark = theme.mode === "dark" || theme.palette.type === "dark";
  const soft = dark ? "rgba(255,255,255,0.08)" : "#F1F3F6";
  return {
    root: {
      position: "relative",
      display: "flex",
      flexDirection: "column",
      height: "100%",
      overflow: "hidden",
      borderRadius: 0,
      backgroundColor: theme.palette.background.paper,
    },
    header: {
      flex: "none",
      display: "flex",
      flexDirection: "column",
      gap: 10,
      padding: "14px 14px 10px",
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    titleRow: { display: "flex", alignItems: "center", gap: 8 },
    title: { margin: 0, fontSize: 20, fontWeight: 700, flexGrow: 1, color: theme.palette.text.primary },
    search: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      height: 40,
      padding: "0 12px",
      borderRadius: 10,
      backgroundColor: soft,
      color: theme.palette.text.secondary,
    },
    searchInput: { flexGrow: 1, fontSize: 14, color: theme.palette.text.primary },
    chipsRow: { display: "flex", alignItems: "center", gap: 8 },
    chips: {
      flexGrow: 1,
      minWidth: 0,
      display: "flex",
      gap: 6,
      overflowX: "auto",
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": { display: "none" },
    },
    chip: {
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      gap: 6,
      height: 32,
      padding: "0 12px",
      borderRadius: 16,
      border: "1px solid transparent",
      backgroundColor: soft,
      color: theme.palette.text.primary,
      fontSize: 13,
      fontWeight: 600,
      fontFamily: "inherit",
      whiteSpace: "nowrap",
      cursor: "pointer",
    },
    chipActive: {
      borderColor: theme.palette.primary.main,
      color: dark ? "#FFFFFF" : theme.palette.primary.main,
      backgroundColor: dark ? "rgba(59,130,246,0.25)" : "#DDE2EE",
    },
    chipCount: { fontSize: 12, fontWeight: 700 },
    filterBtn: {
      flexShrink: 0,
      position: "relative",
      border: `1px solid ${theme.palette.divider}`,
      width: 36,
      height: 36,
    },
    filterBadge: {
      position: "absolute",
      top: -4,
      right: -4,
      minWidth: 18,
      height: 18,
      padding: "0 4px",
      boxSizing: "border-box",
      borderRadius: 9,
      backgroundColor: theme.palette.primary.main,
      color: "#FFFFFF",
      fontSize: 11,
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    filterPanel: { width: 300, padding: 14, display: "flex", flexDirection: "column", gap: 6 },
    filterHead: { display: "flex", alignItems: "center", marginBottom: 4 },
    filterTitle: { flexGrow: 1, fontSize: 15, fontWeight: 700 },
    filterClear: {
      border: "none",
      background: "transparent",
      color: theme.palette.primary.main,
      fontWeight: 600,
      fontSize: 13,
      cursor: "pointer",
      fontFamily: "inherit",
    },
    filterSection: {
      marginTop: 8,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: 0.4,
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
    },
    filterRow: { display: "flex", alignItems: "center", fontSize: 14 },
    filterList: { maxHeight: 180, overflowY: "auto" },
    body: { flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" },
    resultsBox: { flex: "none", maxHeight: "60%", overflowY: "auto", marginBottom: 8, padding: "0 8px" },
    fab: { position: "absolute", right: 18, bottom: 18, zIndex: 5 },
  };
});

const pane = (visible) => ({
  display: visible ? "flex" : "none",
  flex: 1,
  height: "100%",
  flexDirection: "column",
  minHeight: 0,
});

const TicketsManagerSimple = () => {
  const classes = useStyles();
  const puedeVerAgentesIA = getAccessSnapshot().can("prompts:ver");
  const {
    aiAgentsCount,
    allTicketsCount,
    applyPanelStyle,
    closeTicketsOption,
    confirmationModalOpen,
    filterLoading,
    filterResults,
    globalSearchLoading,
    globalSearchResults,
    groupsCount,
    handleCloseOrOpenTicket,
    handleClosedTickets,
    handleConfirmCloseAllTickets,
    handleNewSearch,
    handleNewTicket,
    handleOpenTickets,
    handleResolveTickets,
    handleSelectTicket,
    handleShowAllTickets,
    hasTicketsToClose,
    localSearchParam,
    newTicketModalOpen,
    openCount,
    pendingCount,
    searchOnMessages,
    searchParam,
    selectedQueueIds,
    selectedTagIds,
    setCloseTicketsOption,
    setConfirmationModalOpen,
    setSearchOnMessages,
    setSelectedQueueIds,
    setSelectedTagIds,
    setTabOpen,
    showAllTickets,
    tab,
    tabOpen,
    tags,
    user,
  } = useTicketsManagerTabs();

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [filterAnchor, setFilterAnchor] = useState(null);

  const isAdmin = user?.profile === "admin";
  const current = tab === "closed" ? "closed" : tabOpen;

  const chips = [
    { key: "open", label: i18n.t("tickets.simple.chipOpen"), count: openCount },
    { key: "pending", label: i18n.t("tickets.simple.chipPending"), count: pendingCount },
    showAllTickets && { key: "all", label: i18n.t("ticketsList.allHeader"), count: allTicketsCount },
    user?.allowGroup && { key: "groups", label: i18n.t("tickets.simple.chipGroups"), count: groupsCount },
    puedeVerAgentesIA && { key: "ai-agents", label: i18n.t("tickets.simple.chipAi"), count: aiAgentsCount },
    { key: "closed", label: i18n.t("tickets.simple.chipClosed"), count: null },
  ].filter(Boolean);

  const pickChip = (key) => {
    if (key === "closed") {
      handleClosedTickets();
      return;
    }
    handleOpenTickets();
    setTabOpen(key);
  };

  const toggleId = (setter) => (id) =>
    setter((prev) => (prev.indexOf(id) > -1 ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleQueue = toggleId(setSelectedQueueIds);
  const toggleTag = toggleId(setSelectedTagIds);

  const activeFilters =
    (selectedQueueIds?.length || 0) + (selectedTagIds?.length || 0) + (searchOnMessages ? 1 : 0);

  const queueFilter = filterResults ? selectedQueueIds : undefined;

  return (
    <Paper elevation={0} square className={classes.root}>
      <NewTicketModal modalOpen={newTicketModalOpen} onClose={handleCloseOrOpenTicket} />

      <div className={classes.header}>
        <div className={classes.titleRow}>
          <h1 className={classes.title}>{i18n.t("tickets.simple.title")}</h1>
          <IconButton
            size="small"
            aria-label={i18n.t("tickets.simple.moreOptions")}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                handleResolveTickets();
              }}
            >
              {i18n.t("tickets.simple.resolveBulk")}
            </MenuItem>
            {isAdmin && (
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  handleShowAllTickets();
                }}
              >
                <ListItemText primary={i18n.t("ticketsList.allHeader")} />
                {showAllTickets && (
                  <ListItemIcon style={{ minWidth: 0, marginLeft: 12 }}>
                    <CheckIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                )}
              </MenuItem>
            )}
          </Menu>
        </div>

        <label className={classes.search}>
          <SearchIcon fontSize="small" />
          <InputBase
            className={classes.searchInput}
            placeholder={i18n.t("tickets.simple.searchPlaceholder")}
            type="search"
            value={localSearchParam}
            onChange={handleNewSearch}
            inputProps={{ "aria-label": i18n.t("tickets.simple.searchPlaceholder") }}
          />
        </label>

        <div className={classes.chipsRow}>
          <div className={classes.chips} role="tablist">
            {chips.map((c) => (
              <button
                key={c.key}
                type="button"
                role="tab"
                aria-selected={current === c.key}
                className={`${classes.chip} ${current === c.key ? classes.chipActive : ""}`}
                onClick={() => pickChip(c.key)}
              >
                <span>{c.label}</span>
                {c.count > 0 && <span className={classes.chipCount}>{c.count}</span>}
              </button>
            ))}
          </div>
          <IconButton
            className={classes.filterBtn}
            aria-label={i18n.t("tickets.simple.filters")}
            onClick={(e) => setFilterAnchor(e.currentTarget)}
          >
            <FilterListIcon fontSize="small" />
            {activeFilters > 0 && <span className={classes.filterBadge}>{activeFilters}</span>}
          </IconButton>
        </div>
      </div>

      <Popover
        open={Boolean(filterAnchor)}
        anchorEl={filterAnchor}
        onClose={() => setFilterAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <div className={classes.filterPanel}>
          <div className={classes.filterHead}>
            <span className={classes.filterTitle}>{i18n.t("tickets.simple.filters")}</span>
            <button
              type="button"
              className={classes.filterClear}
              onClick={() => {
                setSelectedQueueIds([]);
                setSelectedTagIds([]);
                setSearchOnMessages(false);
              }}
            >
              {i18n.t("tickets.simple.clearFilters")}
            </button>
          </div>
          <label className={classes.filterRow}>
            <span style={{ flexGrow: 1 }}>{i18n.t("tickets.simple.searchInMessages")}</span>
            <Switch
              size="small"
              color="primary"
              checked={searchOnMessages}
              onChange={(e) => setSearchOnMessages(e.target.checked)}
            />
          </label>

          {isAdmin && (
            <>
              <div className={classes.filterSection}>{i18n.t("tickets.simple.queues")}</div>
              <div className={classes.filterList}>
                {(user?.queues || []).length === 0 && (
                  <div className={classes.filterRow}>{i18n.t("tickets.managerTabs.emptyQueues")}</div>
                )}
                {(user?.queues || []).map((q) => (
                  <label key={q.id} className={classes.filterRow}>
                    <Checkbox
                      size="small"
                      style={{ color: q.color || undefined }}
                      checked={selectedQueueIds.indexOf(q.id) > -1}
                      onChange={() => toggleQueue(q.id)}
                    />
                    {q.name}
                  </label>
                ))}
              </div>
            </>
          )}

          <div className={classes.filterSection}>{i18n.t("tickets.simple.tags")}</div>
          <div className={classes.filterList}>
            {tags.length === 0 && (
              <div className={classes.filterRow}>{i18n.t("tickets.managerTabs.emptyTags")}</div>
            )}
            {tags.map((t) => (
              <label key={t.id} className={classes.filterRow}>
                <Checkbox
                  size="small"
                  style={{ color: t.color || undefined }}
                  checked={selectedTagIds.indexOf(t.id) > -1}
                  onChange={() => toggleTag(t.id)}
                />
                {t.name}
              </label>
            ))}
          </div>
        </div>
      </Popover>

      <div className={classes.body}>
        {globalSearchResults && (
          <div className={classes.resultsBox}>
            <SearchResultsPanel
              results={globalSearchResults}
              searchParam={localSearchParam}
              loading={globalSearchLoading}
              onTicketClick={handleSelectTicket}
            />
          </div>
        )}
        {!globalSearchResults && filterResults && (
          <div className={classes.resultsBox}>
            <SearchResultsPanel
              results={filterResults}
              searchParam=""
              loading={filterLoading}
              onTicketClick={handleSelectTicket}
              isFilterMode={true}
            />
          </div>
        )}

        {/* Siempre montadas, como en la vista clásica: así ninguna lista pierde eventos de socket. */}
        <div style={pane(current === "open")}>
          <TicketsListCustom
            status="open"
            showAll={isAdmin}
            selectedQueueIds={queueFilter}
            style={applyPanelStyle("open")}
            searchOnMessages={searchOnMessages}
            searchParam={globalSearchResults ? "" : searchParam}
            isActiveTab={current === "open"}
            excludeAIAgents={true}
          />
        </div>
        <div style={pane(current === "pending")}>
          <TicketsListCustom
            status="pending"
            selectedQueueIds={queueFilter}
            style={applyPanelStyle("pending")}
            searchOnMessages={searchOnMessages}
            searchParam={searchParam}
            isActiveTab={current === "pending"}
            excludeAIAgents={true}
          />
        </div>
        {showAllTickets && (
          <div style={pane(current === "all")}>
            <TicketsListCustom
              status="all"
              showAll={isAdmin}
              selectedQueueIds={queueFilter}
              style={applyPanelStyle("all")}
              searchOnMessages={searchOnMessages}
              searchParam={searchParam}
              isActiveTab={current === "all"}
            />
          </div>
        )}
        {user?.allowGroup && (
          <div style={pane(current === "groups")}>
            <TicketsListCustom
              status="group"
              selectedQueueIds={queueFilter}
              style={applyPanelStyle("groups")}
              searchOnMessages={searchOnMessages}
              searchParam={searchParam}
              isActiveTab={current === "groups"}
            />
          </div>
        )}
        <div style={pane(current === "ai-agents")}>
          <TicketsListCustom
            status="ai"
            selectedQueueIds={queueFilter}
            style={applyPanelStyle("ai-agents")}
            aiAgentFilter={true}
            excludeAIAgents={false}
            searchOnMessages={searchOnMessages}
            searchParam={searchParam}
            isActiveTab={current === "ai-agents"}
          />
        </div>
        {current === "closed" && (
          <div style={pane(true)}>
            <TicketsListCustom
              status="closed"
              showAll={isAdmin}
              selectedQueueIds={queueFilter}
              searchOnMessages={searchOnMessages}
              searchParam={searchParam}
              isActiveTab={true}
            />
          </div>
        )}
      </div>

      <Fab
        color="primary"
        size="medium"
        className={classes.fab}
        aria-label={i18n.t("tickets.simple.newChat")}
        onClick={handleNewTicket}
      >
        <AddIcon />
      </Fab>

      <CloseAllTicketsModal
        open={confirmationModalOpen}
        onClose={() => {
          setConfirmationModalOpen(false);
          setCloseTicketsOption("open");
        }}
        onConfirm={handleConfirmCloseAllTickets}
        canClose={hasTicketsToClose()}
        option={closeTicketsOption}
        setOption={setCloseTicketsOption}
        openCount={openCount}
        pendingCount={pendingCount}
      />
    </Paper>
  );
};

export default TicketsManagerSimple;
